import { StickyMainHeader } from "../../common/header/StickyMainHeader";
import {
  Alert,
  Col,
  Container,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import { useSongQuery } from "./useSongQuery";
import { ItemHeader } from "./ItemHeader";
import { LoadingIndicator } from "../../common/LoadingIndicator";
import { AboutSongCard } from "./AboutSongCard";
import { SongnumbersCard } from "./SongnumbersCard";
import { MetadataCard } from "./MetadataCard";
import { ChordsheetCard } from "./ChordsheetCard";
import { SourceFilesCard } from "./SourceFilesCard";
import { DerivedImagesCard } from "./DerivedImagesCard";
import { SongUb } from "../../gql/graphql";
import { useState } from "react";
import Auth from "../../auth/Auth";
import { useTranslation } from "react-i18next";
import { useOnlineOffline } from "../../common/hooks/useOnlineOffline";

interface Props {
  auth: Auth;
  bucket: string;
  itemId: string;
}

const createDummySong = (bucket: string, id: string): SongUb => {
  return {
    __typename: "SongUB",
    author: null,
    bucket,
    createdAt: null,
    id,
    language: null,
    lastUpdatedBy: "",
    license: null,
    chordsheet: null,
    songnumbers: [],
    tags: [],
    title: "",
    tonality: null,
    updatedAt: null,
    versionDescription: null,
    sourceHashes: [],
    imageHashes: [],
  };
};

export function Item({ auth, bucket, itemId }: Props) {
  const { songMaybe, fetching, reexecuteQuery } = useSongQuery(bucket, itemId);
  const [showMetadata, setShowMetadata] = useState(false);
  const { t } = useTranslation();
  const isOnline = useOnlineOffline();

  const [song, isUnsavedNewSong] = songMaybe
    ? [songMaybe, false]
    : [createDummySong(bucket, itemId), true];

  const areSongFieldsDisabled = isUnsavedNewSong || !isOnline;
  const isTitleDisabled = !isOnline;

  return (
    <Container fluid={true}>
      <StickyMainHeader>
        <ItemHeader
          showMetadata={showMetadata}
          onToggleMetadata={() => setShowMetadata(!showMetadata)}
          updatedAt={song.updatedAt}
        />
      </StickyMainHeader>

      {!isOnline && (
        <Alert color="info" fade={false} className="mb-2">
          {t("items.edit.offlineReadOnly")}
        </Alert>
      )}

      {fetching ? (
        <LoadingIndicator text={t("items.loadingItem")} />
      ) : (
        <>
          <Row className="g-2 mb-2">
            <Col lg={7}>
              <Row className={"mb-2"}>
                <Col>
                  <AboutSongCard
                    song={song}
                    isDisabled={areSongFieldsDisabled}
                    isTitleDisabled={isTitleDisabled}
                    onMutationCompleted={() => {
                      //this makes sure that after saving a new song, the data is fetched to replace the dummy by the GraphQL version
                      //However: After a mutation, URQL will automatically update the cache, so reexecuting the query is not necessary
                      if (isUnsavedNewSong) {
                        reexecuteQuery();
                      }
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col lg={5}>
              <Offcanvas
                isOpen={showMetadata}
                toggle={() => setShowMetadata(!showMetadata)}
                direction="end"
              >
                <OffcanvasHeader toggle={() => setShowMetadata(!showMetadata)}>
                  {t("common.details")}
                </OffcanvasHeader>
                <OffcanvasBody>
                  <MetadataCard song={song} />
                </OffcanvasBody>
              </Offcanvas>
              <Row>
                <Col>
                  <SongnumbersCard
                    song={song}
                    isDisabled={areSongFieldsDisabled}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12}>
              <ChordsheetCard song={song} isDisabled={areSongFieldsDisabled} />
            </Col>
            <Col xs={12} xl={4}>
              <SourceFilesCard
                auth={auth}
                song={song}
                isDisabled={areSongFieldsDisabled}
                onUploadCompleted={() => reexecuteQuery()}
              />
            </Col>
            <Col xs={12} xl={8}>
              <DerivedImagesCard
                auth={auth}
                song={song}
                reexecuteQuery={reexecuteQuery}
                isDisabled={!isOnline}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
